import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useAuthStore } from 'src/stores/AuthStore/AuthStore';
import { userLogout } from 'src/services/auth';
import { PATHS } from 'src/constants/navigateRoutes';
import { useNavigate } from 'react-router-dom';
import { CustomDivider } from '../custom-divider/CustomDivider';
import styles from '../../scss/_variables.scss';
import { useTranslation } from 'react-i18next';

export interface ProfileModalProp {
  clickId: string;
  buttonId: string;
  revamp?: boolean;
}

const ProfileModal: React.FC<ProfileModalProp> = ({
  clickId,
  buttonId,
  revamp = false,
}) => {
  const { getUserDetails, logout } = useAuthStore();
  const userDetails = getUserDetails();
  const navigate = useNavigate();
  const [profileEl, setProfileEl] = useState<HTMLButtonElement | null>(null);
  const { t } = useTranslation();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setProfileEl(event.currentTarget);
  };

  const handleClose = () => {
    setProfileEl(null);
  };

  const open = Boolean(profileEl);

  const handleSignOut = async () => {
    await userLogout();
    logout();
    navigate(PATHS.unAuthorized);
  };

  const openSettings = () => {
    handleClose();
    navigate(`${PATHS.settings}?tab=profile`);
  };

  const openLearn = () => {
    handleClose();
    navigate(PATHS.help);
  };

  return (
    <div>
      <IconButton
        id={buttonId}
        aria-describedby={clickId}
        aria-label="profile"
        onClick={handleClick}
      >
        <Avatar
          alt={userDetails?.displayName}
          src={userDetails?.profilePicture}
        />
      </IconButton>
      <Popover
        id={clickId}
        open={open}
        anchorEl={profileEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: revamp ? 'background.paper' : styles.sealBrown,
              color: revamp ? 'text.primary' : styles.colorWhite,
              maxWidth: 350,
              minWidth: revamp ? 280 : undefined,
              padding: revamp
                ? { sm: 1.5, xs: 1.5 }
                : { sm: '24px 32px', xs: '24px' },
              borderRadius: revamp ? 2 : '16px',
              border: revamp ? '1px solid' : undefined,
              borderColor: revamp ? 'divider' : undefined,
              boxShadow: revamp
                ? '0 16px 40px rgba(17, 24, 39, 0.14)'
                : undefined,
            },
          },
        }}
      >
        {revamp ? (
          <Stack gap={1}>
            <Box px={1} py={0.75}>
              <Typography variant="body2" fontWeight={700}>
                {userDetails?.displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {userDetails?.email}
              </Typography>
            </Box>
            <CustomDivider />
            <Button
              variant="text"
              color="inherit"
              startIcon={<SettingsOutlinedIcon />}
              onClick={openSettings}
              sx={{ justifyContent: 'flex-start' }}
            >
              {t('revamp_shell.account_settings', {
                defaultValue: 'Account settings',
              })}
            </Button>
            <Button
              component="a"
              href="mailto:support@hanabitech.com"
              variant="text"
              color="inherit"
              startIcon={<MailOutlineIcon />}
              onClick={handleClose}
              sx={{ justifyContent: 'flex-start' }}
            >
              {t('revamp_shell.contact_support', {
                defaultValue: 'Contact support',
              })}
            </Button>
            <Button
              variant="text"
              color="inherit"
              startIcon={<MenuBookOutlinedIcon />}
              onClick={openLearn}
              sx={{ justifyContent: 'flex-start' }}
            >
              {t('revamp_shell.learn_hana', {
                defaultValue: 'Learn Hana',
              })}
            </Button>
            <Button
              id="user_sign_out"
              variant="text"
              color="inherit"
              startIcon={<LogoutOutlinedIcon />}
              onClick={handleSignOut}
              sx={{ justifyContent: 'flex-start' }}
              aria-label={t('revamp_shell.sign_out', {
                defaultValue: 'Sign out',
              })}
            >
              {t('revamp_shell.sign_out', { defaultValue: 'Sign out' })}
            </Button>
          </Stack>
        ) : (
          <>
            <Typography
              variant="body2"
              sx={{
                fontSize: '20px',
                lineHeight: '30px',
                fontWeight: 700,
              }}
            >
              {userDetails?.displayName}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '16px',
                lineHeight: '24px',
                paddingTop: '8px',
                color: styles.mutedGray,
              }}
            >
              {userDetails?.email}
            </Typography>
            <CustomDivider sx={{ margin: '8px 0' }} />
            <Button
              id="user_sign_out"
              variant="outlined"
              onClick={handleSignOut}
              sx={{ marginTop: '12px' }}
              aria-label="Sign out"
            >
              Sign out
            </Button>
          </>
        )}
      </Popover>
    </div>
  );
};

export default ProfileModal;
